import { useMemo, useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { RotateCcw } from 'lucide-react'
import { formatearAntiguedad, formatearFechaHora } from '@/lib/fecha'
import { Boton } from '@/ui/Boton'
import { ENCF, FechaHora } from '@/ui/Numero'
import { TablaDatos } from '@/ui/tabla/TablaDatos'
import type { TrabajoFallido } from '@/dominio/consola/tipos'
import { traducirErrorConsola, useFallidos } from '@/dominio/consola/useConsola'
import { EncabezadoPantalla } from '../-pantalla'
import { entero } from './-comun'
import { DialogoReinyectar, encfDeTrabajo } from './clientes/-pestanas/Colas'

/**
 * Bandeja de fallidos de **todos** los clientes (`docs/09` §5.5).
 *
 * Un trabajo fallido agotó sus reintentos: no se va a arreglar solo. Aquí se
 * ven juntos porque después de una caída de DGII o del proveedor los fallidos
 * llegan de muchos clientes a la vez, y resolverlos cliente por cliente es una
 * tarde entera. Se reinyectan en lote, con un solo motivo.
 */

const LIMITES = [100, 250, 500] as const
type Limite = (typeof LIMITES)[number]

export const Route = createFileRoute('/consola/fallidos')({
  validateSearch: (busqueda: Record<string, unknown>): { limite?: Limite } => {
    const n = Number(busqueda.limite)
    return { limite: (LIMITES as readonly number[]).includes(n) && n !== 100 ? (n as Limite) : undefined }
  },
  component: BandejaFallidos,
})

function BandejaFallidos() {
  const { limite = 100 } = Route.useSearch()
  const navegar = Route.useNavigate()
  const consulta = useFallidos(limite)
  const [seleccion, setSeleccion] = useState<RowSelectionState>({})
  const [aReinyectar, setAReinyectar] = useState<TrabajoFallido[] | null>(null)
  const filas = consulta.data ?? []

  const columnas = useMemo<ColumnDef<TrabajoFallido, unknown>[]>(
    () => [
      {
        id: 'actualizadoEl',
        accessorFn: (f) => f.actualizadoEl,
        header: 'Último intento',
        enableSorting: false,
        meta: { etiqueta: 'Último intento', numerico: true, ancho: '150px', exportar: (f) => formatearFechaHora(f.actualizadoEl) },
        cell: ({ row }) => (
          <span title={`hace ${formatearAntiguedad(row.original.actualizadoEl)}`}>
            <FechaHora valor={row.original.actualizadoEl} />
          </span>
        ),
      },
      {
        id: 'cliente',
        accessorFn: (f) => f.razonSocial,
        header: 'Cliente',
        enableSorting: false,
        meta: { etiqueta: 'Cliente', exportar: (f) => f.razonSocial },
        cell: ({ row }) => (
          <Link
            to="/consola/clientes/$id"
            params={{ id: row.original.tenantId }}
            search={{ pestana: 'colas' }}
            onClick={(e) => e.stopPropagation()}
            className="block truncate text-gris-900 hover:text-marca hover:underline"
            title={row.original.razonSocial}
          >
            {row.original.razonSocial}
          </Link>
        ),
      },
      {
        id: 'trabajo',
        accessorFn: (f) => `${f.cola} ${f.tipoTrabajo}`,
        header: 'Trabajo',
        enableSorting: false,
        meta: { etiqueta: 'Trabajo', ancho: '200px', exportar: (f) => `${f.cola} · ${f.tipoTrabajo}` },
        cell: ({ row }) => (
          <span className="truncate">
            <span className="mono text-etiqueta text-gris-600">{row.original.cola}</span> · {row.original.tipoTrabajo}
          </span>
        ),
      },
      {
        id: 'encf',
        accessorFn: (f) => encfDeTrabajo(f) ?? '',
        header: 'e-NCF',
        enableSorting: false,
        meta: { etiqueta: 'e-NCF', numerico: true, ancho: '165px', exportar: (f) => encfDeTrabajo(f) },
        cell: ({ row }) => {
          const encf = encfDeTrabajo(row.original)
          return encf !== null ? <ENCF valor={encf} /> : <span className="text-gris-400">—</span>
        },
      },
      {
        id: 'intentos',
        accessorFn: (f) => f.intentos,
        header: 'Intentos',
        enableSorting: false,
        meta: { etiqueta: 'Intentos', numerico: true, ancho: '90px' },
        cell: ({ row }) => entero(row.original.intentos),
      },
      {
        id: 'fallo',
        accessorFn: (f) => f.ultimoError ?? '',
        header: 'Fallo',
        enableSorting: false,
        meta: {
          etiqueta: 'Último fallo',
          exportar: (f) => [f.ultimoCodigoFallo, f.ultimoError].filter(Boolean).join(' · '),
        },
        cell: ({ row }) => (
          <span className="block truncate text-gris-700" title={row.original.ultimoError ?? undefined}>
            {row.original.ultimoCodigoFallo ? (
              <span className="mono mr-1 text-gris-900">{row.original.ultimoCodigoFallo}</span>
            ) : null}
            {row.original.ultimoError ?? 'sin detalle'}
          </span>
        ),
      },
      {
        id: 'acciones',
        header: () => <span className="solo-lectores">Acciones</span>,
        enableSorting: false,
        enableHiding: false,
        meta: { etiqueta: 'Acciones', ancho: '130px', exportar: () => null },
        cell: ({ row }) => (
          <span className="flex justify-end">
            <Boton
              tamano="menor"
              variante="secundario"
              icono={<RotateCcw />}
              onClick={(e) => {
                e.stopPropagation()
                setAReinyectar([row.original])
              }}
            >
              Reinyectar
            </Boton>
          </span>
        ),
      },
    ],
    [],
  )

  return (
    <div className="flex min-h-0 flex-col gap-4">
      <EncabezadoPantalla
        titulo="Fallidos"
        descripcion="Trabajos que agotaron sus reintentos, de todos los clientes. Reinyectar no crea documentos ni consume secuencias: vuelve a intentar lo mismo."
      />

      <TablaDatos<TrabajoFallido>
        idTabla="consola.fallidos"
        columnas={columnas}
        datos={filas}
        obtenerId={(f) => f.id}
        cargando={consulta.isLoading}
        error={consulta.isError ? traducirErrorConsola(consulta.error, 'cargar los fallidos') : null}
        onReintentar={() => void consulta.refetch()}
        vacio={{ variante: 'resuelto', mensaje: 'Sin trabajos fallidos en ningún cliente.' }}
        bordeFila={() => 'border-l-rojo'}
        seleccionable
        seleccion={seleccion}
        onSeleccionChange={setSeleccion}
        accionesSeleccion={(ids) => (
          <Boton
            tamano="menor"
            variante="secundario"
            icono={<RotateCcw />}
            onClick={() => setAReinyectar(filas.filter((f) => ids.includes(f.id)))}
          >
            Reinyectar {ids.length}
          </Boton>
        )}
        nombreExportacion="fallidos-plataforma"
        filtros={
          <label className="flex items-center gap-1 text-etiqueta text-gris-600">
            Mostrar
            <select
              value={limite}
              onChange={(e) => {
                const n = Number(e.target.value) as Limite
                setSeleccion({})
                void navegar({ search: { limite: n === 100 ? undefined : n }, replace: true })
              }}
              className="h-[var(--alto-control-menor)] rounded-md border border-gris-500 bg-white px-1 text-etiqueta text-gris-900"
            >
              {LIMITES.map((l) => (
                <option key={l} value={l}>
                  los {l} más recientes
                </option>
              ))}
            </select>
            {filas.length >= limite ? (
              <span className="ml-2 text-gris-900">hay más de {limite}: se ven los más recientes</span>
            ) : null}
          </label>
        }
      />

      {aReinyectar !== null && aReinyectar.length > 0 ? (
        <DialogoReinyectar
          trabajos={aReinyectar}
          onCerrar={() => setAReinyectar(null)}
          onTerminado={() => setSeleccion({})}
        />
      ) : null}
    </div>
  )
}
